<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { gradeConfig, getBatteryConfig, sideConfig } from '@/lib/product'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { ArrowUpRight, Check, Layers } from 'lucide-vue-next'
import type { CompetitorGrade } from './catalogDetailTypes'

const { t } = useI18n()

/**
 * Every variant of this device, and how many listings each market holds for it.
 *
 * A grade and a battery type are two independent axes, so the shape of the answer is
 * a matrix rather than a list: "is B worse than A everywhere, or only on used
 * batteries?" is a glance down a column instead of a scan through rows.
 *
 * One grid, not one per market. The variants are the same variants either way, so two
 * tables meant reading the same row twice a screen apart to answer "what does this
 * device look like on both sides?" — which is the question the card exists for. The
 * counts sit together in the cell and are coloured, never summed: they are measured
 * against different vendors and adding them would invent a market.
 */
const props = defineProps<{
    sides: ('resell' | 'buyback')[]
    gradesBySide: Record<string, CompetitorGrade[]>
    loading: boolean
    currentUniqueKey: string
}>()

const gradeOrder = Object.keys(gradeConfig)
const batteryOrder = ['new', 'std']

const key = (grade: string, battery: string) => `${grade}-${battery}`

/** A side the item was actually counted on; it earns a figure in every cell. */
const countedSides = computed(() => props.sides.filter((side) => (props.gradesBySide[side] ?? []).length > 0))

/**
 * The variants worth a cell: one either market has listings for, plus the one being
 * viewed, which belongs on the page whether or not anybody stocks it.
 *
 * Held as a map because a variant is the same catalog item on both sides — the counts
 * differ, the identity does not.
 */
const combos = computed(() => {
    const byKey = new Map<string, CompetitorGrade>()

    for (const side of props.sides) {
        for (const entry of props.gradesBySide[side] ?? []) {
            if (entry.counter === 0 && entry.unique_key !== props.currentUniqueKey) {
                continue
            }

            if (!byKey.has(key(entry.grade, entry.battery_type))) {
                byKey.set(key(entry.grade, entry.battery_type), entry)
            }
        }
    }

    return byKey
})

const ordered = (values: Set<string>, order: string[]) =>
    [...values].sort((a, b) => {
        const rank = order.indexOf(a) - order.indexOf(b)

        return rank !== 0 ? rank : a.localeCompare(b)
    })

/** The axes are the union across both markets, so a variant has one place on the grid. */
const grades = computed(() => ordered(new Set([...combos.value.values()].map((entry) => entry.grade)), gradeOrder))

const batteries = computed(() =>
    ordered(new Set([...combos.value.values()].map((entry) => entry.battery_type)), batteryOrder),
)

const countersBySide = computed(
    () =>
        new Map(
            countedSides.value.map((side) => [
                side,
                new Map(
                    (props.gradesBySide[side] ?? []).map((entry) => [
                        key(entry.grade, entry.battery_type),
                        entry.counter,
                    ]),
                ),
            ]),
        ),
)

const rows = computed(() =>
    batteries.value.map((battery) => ({
        battery,
        cells: grades.value.map((grade) => {
            const combo = combos.value.get(key(grade, battery))

            return {
                key: key(grade, battery),
                catalogItemId: combo?.catalog_item_id ?? null,
                isCurrent: combo?.unique_key === props.currentUniqueKey,
                counts: countedSides.value.map((side) => ({
                    side,
                    // Missing is not zero: this market never saw that variant.
                    counter: countersBySide.value.get(side)?.get(key(grade, battery)) ?? null,
                })),
            }
        }),
    })),
)

/**
 * The two market totals and the account's own coverage, which is why they sit apart
 * in the header: the first two count listings other people hold, the third counts
 * what this account carries, and `catalog_item_id` is the only thing that says so.
 */
const totals = computed(() =>
    countedSides.value.map((side) => ({
        side,
        total: [...(countersBySide.value.get(side)?.values() ?? [])].reduce((sum, counter) => sum + counter, 0),
    })),
)

const inCatalogCount = computed(
    () => [...combos.value.values()].filter((entry) => entry.catalog_item_id !== null).length,
)
</script>

<template>
    <Card v-if="loading || combos.size > 0">
        <CardHeader class="flex flex-row flex-wrap items-center justify-between gap-x-6 gap-y-3 space-y-0">
            <CardTitle class="flex items-center gap-2">
                <Layers class="size-4" />
                {{ t('catalog.details.variants') }}
            </CardTitle>

            <div v-if="!loading" class="flex flex-wrap items-center gap-x-5 gap-y-2">
                <!--
                    Totals and legend in one: the colour and icon beside each figure are
                    the colour and icon inside every cell, so naming the markets here is
                    also what makes the pair of numbers in a cell readable.
                -->
                <div class="flex flex-wrap items-center gap-x-4 gap-y-1">
                    <span class="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {{ t('catalog.details.competitorGrades.activeListings') }}
                    </span>
                    <span
                        v-for="entry in totals"
                        :key="entry.side"
                        class="inline-flex items-center gap-1.5"
                        :class="sideConfig[entry.side].accent.text"
                    >
                        <component :is="sideConfig[entry.side].icon" class="size-3" />
                        <span class="text-[10px] font-semibold uppercase tracking-wider">
                            {{ sideConfig[entry.side].label }}
                        </span>
                        <span class="text-xs font-semibold tabular-nums">{{ entry.total }}</span>
                    </span>
                </div>

                <div class="flex items-center gap-2 border-l pl-5">
                    <span class="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {{ t('catalog.details.competitorGrades.inCatalog') }}
                    </span>
                    <span class="text-xs font-semibold tabular-nums">
                        {{
                            t('catalog.details.competitorGrades.inCatalogCount', {
                                count: inCatalogCount,
                                total: combos.size,
                            })
                        }}
                    </span>
                </div>
            </div>
        </CardHeader>

        <CardContent>
            <div v-if="loading" class="space-y-2">
                <Skeleton class="h-6 w-full" />
                <Skeleton class="h-6 w-full" />
            </div>

            <!--
                Fixed layout, so the grade columns are equal shares of what the battery
                column leaves rather than as wide as their headings — "Very Good" would
                otherwise take half again what "Good" does, and a matrix whose columns
                disagree is harder to read down.
            -->
            <Table v-else class="table-fixed">
                <TableHeader>
                    <TableRow class="hover:bg-transparent">
                        <TableHead class="w-28">
                            {{ t('catalog.details.competitorGrades.battery') }}
                        </TableHead>
                        <TableHead v-for="grade in grades" :key="grade" class="text-center">
                            {{ gradeConfig[grade]?.label ?? grade }}
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    <TableRow v-for="row in rows" :key="row.battery" class="hover:bg-transparent">
                        <TableCell>
                            <span
                                class="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-xs font-medium"
                            >
                                <component :is="getBatteryConfig(row.battery).icon" class="size-3" />
                                {{ getBatteryConfig(row.battery).label }}
                            </span>
                        </TableCell>

                        <!--
                            The box is the link: a variant is a catalog item, and its counts
                            are what you click to go and look at it. Which of the three
                            states it is in has to be legible at rest, not on hover — so the
                            outline and the caption are always drawn and only their emphasis
                            changes, from a faint dashed box for a variant nobody here
                            carries to a filled one for the variant being viewed.
                        -->
                        <TableCell v-for="cell in row.cells" :key="cell.key" class="px-1.5">
                            <component
                                :is="cell.catalogItemId && !cell.isCurrent ? 'router-link' : 'div'"
                                :to="
                                    cell.catalogItemId && !cell.isCurrent
                                        ? `/dashboard/catalog/${cell.catalogItemId}`
                                        : undefined
                                "
                                :title="
                                    cell.isCurrent
                                        ? t('catalog.details.competitorGrades.current')
                                        : cell.catalogItemId
                                          ? t('catalog.details.competitorGrades.seeInCatalog')
                                          : t('catalog.details.notInCatalog')
                                "
                                class="group block rounded-md border px-2 py-1.5 transition-colors"
                                :class="
                                    cell.isCurrent
                                        ? 'border-primary/50 bg-secondary'
                                        : cell.catalogItemId
                                          ? 'cursor-pointer border-border hover:bg-secondary'
                                          : 'border-dashed border-border/50'
                                "
                            >
                                <!--
                                    Side by side, never added up: the two are counted over
                                    different vendors, so a sum would be a number no market
                                    ever held.
                                -->
                                <span class="flex items-center justify-center gap-3">
                                    <span
                                        v-for="count in cell.counts"
                                        :key="count.side"
                                        class="inline-flex items-center gap-1 text-sm font-semibold tabular-nums"
                                        :class="
                                            count.counter === null
                                                ? 'text-muted-foreground/60'
                                                : sideConfig[count.side].accent.text
                                        "
                                        :title="`${sideConfig[count.side].label} — ${t('catalog.details.competitorGrades.deals')}`"
                                    >
                                        <component :is="sideConfig[count.side].icon" class="size-3 shrink-0" />
                                        {{ count.counter ?? '—' }}
                                    </span>
                                </span>

                                <!--
                                    Reserved on every cell, filled on two of them: the caption
                                    is what says "this one is in your catalog" without a
                                    hover, and giving the line to cells that have nothing to
                                    say keeps one labelled cell from growing its whole row.
                                -->
                                <span
                                    class="mt-1 flex h-3 items-center justify-center gap-0.5 text-[10px] font-semibold uppercase tracking-wide"
                                >
                                    <template v-if="cell.isCurrent">
                                        <Check class="size-2.5" />
                                        {{ t('catalog.details.competitorGrades.current') }}
                                    </template>
                                    <span
                                        v-else-if="cell.catalogItemId"
                                        class="inline-flex items-center gap-0.5 text-muted-foreground group-hover:text-foreground"
                                    >
                                        {{ t('catalog.details.competitorGrades.view') }}
                                        <ArrowUpRight class="size-2.5" />
                                    </span>
                                </span>
                            </component>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </CardContent>
    </Card>
</template>
